///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The webcam layer keeps the whole catalog (~38k cameras) in a compact
/// cluster index and draws it through one WebcamCloudNode; real scene nodes
/// (WebcamNode) exist only for cameras the user is interacting with. Full
/// rows are fetched per camera as they come on screen and kept in a small
/// LRU, so the layer's memory doesn't scale with the catalog's strings.
///

import { Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import { registerWebcamStatsProvider } from "@/earthbrowser/utils/tileStatsRegistry"
import type { WebcamCategory, WebcamEntry, WebcamStatus, WebcamStreamType } from "@shared-types/webcam"
import type { RenderActionSet } from "@stovokor/engine/Renderer"
import type { RenderState } from "@stovokor/engine/RenderState"
import { DatasetLayer, type DatasetSceneObject, type DatasetFeature, type DatasetManifest, type DatasetSyncConfig } from "../DatasetLayer"
import { WebcamClusterIndex, type WebcamCatalogLeaf } from "./WebcamClusterIndex"
import { WebcamCloudNode, type WebcamCloudHost } from "./WebcamCloudNode"
import { WebcamNode } from "./WebcamNode"
import { WebcamStationsTable } from "./WebcamStationsTable"

export interface WebcamFeature extends WebcamEntry, DatasetFeature {
	id: string
}

/** Raw Arrow row shape for the webcam stations feed (only fields we read) */
interface WebcamRow {
	id: unknown
	source_id: unknown
	name: unknown
	lat: unknown
	lon: unknown
	country_code: string | null | undefined
	region: string | null | undefined
	category: unknown
	image_url: string | null | undefined
	stream_url: string | null | undefined
	stream_type: string | null | undefined
	refresh_seconds: unknown
	status: unknown
	last_success: string | null | undefined
	thumb_url: unknown
	attribution: unknown
	attribution_url: string | null | undefined
}

// Full rows kept for cameras recently on screen or interacted with
const cEntryCacheSize = 4000
// A node loaded for a deep link / search keeps living this long without
// focus, covering the focus flight's arrival; after that it is pruned like
// any other node once nothing references it
const cPinnedGraceMs = 15_000
// Retry interval for the catalog load while the table is empty
const cEmptyRetryMs = 5_000

export class WebcamsDatasetLayer extends DatasetLayer<WebcamFeature> implements WebcamCloudHost {
	private unsubscribeRedux?: () => void
	private lastVisibleState: boolean = true

	private readonly cloud: WebcamCloudNode
	private index?: WebcamClusterIndex
	private loading = false
	private loadGeneration = 0
	private nextLoadAt = 0

	// LRU of full rows by leaf index (insertion order = recency)
	private readonly entries = new Map<number, WebcamEntry>()
	private readonly inFlight = new Set<number>()
	// Creation time of each live WebcamNode, by camera id
	private readonly materializedAt = new Map<string, number>()

	constructor(context: EarthBrowserContext) {
		super(context, 'webcams')

		this.cloud = new WebcamCloudNode(context, this)
		this.addNode(this.cloud)
		this.subscribeToRedux()
		registerWebcamStatsProvider(() => this.debugSnapshot())
	}

	// ==================== Render Loop ====================

	override render(state: RenderState): RenderActionSet[] {
		// Hidden layers never load the catalog; the first visible frame does.
		// An empty table (first launch before the sync lands, or mid-replace
		// during ingest) is retried on a timer, and the sync's completion
		// triggers a load through refresh() anyway
		if (this.visible && !this.index && !this.loading && performance.now() >= this.nextLoadAt && Database.getDb()) {
			void this.loadCatalog()
		}
		return super.render(state)
	}

	/**
	 * Load every camera's compact row and build the cluster index. Runs on
	 * first show and after each sync; a newer load supersedes an older one
	 * still in flight.
	 */
	private async loadCatalog(): Promise<void> {
		const gen = ++this.loadGeneration
		this.loading = true
		try {
			const rows = await WebcamStationsTable.getAllCompact()
			if (gen !== this.loadGeneration) return
			if (rows.length === 0) {
				// Keep whatever was showing (a table mid-replace empties
				// briefly) and try again later
				this.nextLoadAt = performance.now() + cEmptyRetryMs
				return
			}
			const leaves: WebcamCatalogLeaf[] = rows.map(r => ({
				id: r.id,
				lat: r.lat,
				lon: r.lon,
				category: r.category,
				hasStream: r.has_stream === 1,
				stale: r.stale === 1,
			}))
			const index = new WebcamClusterIndex(leaves)
			console.log(`[WebcamsDatasetLayer] Indexed ${index.leafCount} webcams into ${index.tierCount} tiers (${index.tiers.map(t => t.count).join(" > ")}) in ${index.buildMs.toFixed(0)} ms`)
			this.index = index
			// Leaf indices are new; re-key the cached rows by id
			const cached = [...this.entries.values()]
			this.entries.clear()
			this.inFlight.clear()
			for (const entry of cached) {
				const leaf = index.leafOf(entry.id)
				if (leaf >= 0) this.entries.set(leaf, entry)
			}
			this.cloud.setIndex(index)
		} catch (error) {
			console.error(`[WebcamsDatasetLayer] Catalog load failed:`, error)
			this.nextLoadAt = performance.now() + cEmptyRetryMs
		} finally {
			if (gen === this.loadGeneration) this.loading = false
		}
	}

	// ==================== WebcamCloudHost ====================

	public entryFor(leaf: number): WebcamEntry | undefined {
		const entry = this.entries.get(leaf)
		if (entry) {
			// Refresh recency
			this.entries.delete(leaf)
			this.entries.set(leaf, entry)
		}
		return entry
	}

	public requestEntries(leaves: number[]): void {
		const index = this.index
		if (!index) return
		const want: number[] = []
		const ids: string[] = []
		for (const leaf of leaves) {
			if (this.entries.has(leaf) || this.inFlight.has(leaf)) continue
			this.inFlight.add(leaf)
			want.push(leaf)
			ids.push(index.ids[leaf])
		}
		if (ids.length === 0) return

		void WebcamStationsTable.getByIds(ids)
			.then(rows => {
				if (this.index !== index) return
				for (const row of rows) {
					const leaf = index.leafOf(row.id)
					if (leaf >= 0) this.cacheEntry(leaf, row)
				}
			})
			.catch(error => console.error(`[WebcamsDatasetLayer] Row fetch failed:`, error))
			.finally(() => {
				for (const leaf of want) this.inFlight.delete(leaf)
			})
	}

	private cacheEntry(leaf: number, entry: WebcamEntry): void {
		if (!this.entries.has(leaf) && this.entries.size >= cEntryCacheSize) {
			const oldest = this.entries.keys().next().value
			if (oldest !== undefined) this.entries.delete(oldest)
		}
		this.entries.delete(leaf)
		this.entries.set(leaf, entry)
	}

	public materialize(leaf: number): WebcamNode | undefined {
		const index = this.index
		if (!index) return undefined
		const id = index.ids[leaf]
		const existing = this.sceneObjects.get(id)
		if (existing) return existing as unknown as WebcamNode
		const entry = this.entryFor(leaf)
		if (!entry) return undefined
		this.addSceneObject(entry)
		this.materializedAt.set(id, performance.now())
		return this.sceneObjects.get(id) as unknown as WebcamNode | undefined
	}

	public nodeFor(leaf: number): WebcamNode | undefined {
		const index = this.index
		if (!index) return undefined
		return this.sceneObjects.get(index.ids[leaf]) as unknown as WebcamNode | undefined
	}

	public pruneMaterialized(keepLeaf: number): void {
		if (this.sceneObjects.size === 0) return
		const keepId = keepLeaf >= 0 && this.index ? this.index.ids[keepLeaf] : undefined
		const now = performance.now()
		for (const [id, obj] of this.sceneObjects) {
			const node = obj as unknown as WebcamNode
			if (id === keepId || node.isFocused || node.isHovered) continue
			if (this.pinnedSceneObjects.has(id)) {
				const since = this.materializedAt.get(id) ?? now
				if (now - since < cPinnedGraceMs) continue
				this.pinnedSceneObjects.delete(id)
			}
			this.removeSceneObject(id)
			this.materializedAt.delete(id)
		}
	}

	public focusLeaf(leaf: number): void {
		const index = this.index
		if (!index) return
		const node = this.materialize(leaf)
		if (node) {
			this.focusNode(node)
			return
		}
		const id = index.ids[leaf]
		void WebcamStationsTable.getById(id).then(entry => {
			if (!entry || this.index !== index) return
			this.cacheEntry(leaf, entry)
			const created = this.materialize(leaf)
			if (created) this.focusNode(created)
		})
	}

	private focusNode(node: WebcamNode): void {
		if (this.context.canFocusEntity(node)) {
			this.context.focusManager.requestFocus(node)
		}
	}

	public zoomTo(lat: number, lon: number, altitudeKm: number): void {
		// A focused camera owns the command chain; hand it back to Earth
		// first (without a flight) so the cluster zoom is what moves
		if (this.context.focusManager.getFocusedNode()) {
			this.context.focusManager.releaseFocus({ preserveCurrentState: true })
		}
		engineBridge.dispatch({
			type: engineActions.sendCameraCommand,
			payload: { command: "flyTo/location", data: { lat, lon, altitude: altitudeKm } },
		})
	}

	public openSheet(tier: number, index: number): void {
		const idx = this.index
		if (!idx) return
		const t = idx.tiers[tier]
		const members = idx.membersOf(tier, index)
		if (members.length === 0) return
		engineBridge.dispatch({
			type: engineActions.setWebcamSheet,
			payload: {
				ids: members.map(leaf => idx.ids[leaf]),
				title: `${members.length.toLocaleString("en-US")} webcams`,
				lat: t.lat[index],
				lon: t.lon[index],
				extentKm: Math.max(t.extentKm[index], 1),
			},
		})
	}

	private debugSnapshot(): Record<string, unknown> {
		const index = this.index
		return {
			...this.cloud.snapshot(),
			leafCount: index?.leafCount ?? 0,
			tierCounts: index?.tiers.map(t => t.count) ?? [],
			buildMs: index?.buildMs ?? null,
			cachedEntries: this.entries.size,
			inFlight: this.inFlight.size,
			materialized: [...this.sceneObjects.keys()],
			loading: this.loading,
		}
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "webcam_stations",
			pollIntervalMs: 60 * 60_000, // New checkpoint is published at most hourly
			needsSync: true,
			supportsDelta: false,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await WebcamStationsTable.create(db)
	}

	public override async getStoredRowCount(datasetId: string): Promise<number | null> {
		if (datasetId === "webcam_stations") return WebcamStationsTable.getCount()
		return null
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[WebcamsDatasetLayer] Downloading stations shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[WebcamsDatasetLayer] Parsed ${rows.length} webcam rows`)

		const webcams = this.parseRows(rows)
		await WebcamStationsTable.replaceAll(webcams)
	}

	public async onSyncComplete(): Promise<void> {
		await this.refresh()
	}

	private parseRows(rows: unknown[]): WebcamEntry[] {
		return (rows as WebcamRow[]).map(r => ({
			id: String(r.id),
			source_id: String(r.source_id),
			name: String(r.name),
			lat: Number(r.lat),
			lon: Number(r.lon),
			country_code: r.country_code ? String(r.country_code) : null,
			region: r.region ? String(r.region) : null,
			category: String(r.category) as WebcamCategory,
			image_url: r.image_url ? String(r.image_url) : null,
			stream_url: r.stream_url ? String(r.stream_url) : null,
			stream_type: r.stream_type ? String(r.stream_type) as WebcamStreamType : null,
			refresh_seconds: Number(r.refresh_seconds),
			status: String(r.status) as WebcamStatus,
			last_success: r.last_success ? String(r.last_success) : null,
			thumb_url: String(r.thumb_url),
			attribution: String(r.attribution),
			attribution_url: r.attribution_url ? String(r.attribution_url) : null,
		}))
	}

	// ==================== Redux Subscription ====================

	private subscribeToRedux(): void {
		this.lastVisibleState = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("webcam") ?? false
		this.visible = this.lastVisibleState

		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const isVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("webcam") ?? false

			if (isVisible !== this.lastVisibleState) {
				this.lastVisibleState = isVisible
				this.visible = isVisible
			}
		})
	}

	// ==================== Scene Objects ====================

	public createSceneObject(feature: WebcamFeature): DatasetSceneObject<WebcamFeature> | undefined {
		return new WebcamNode(this.context, feature) as unknown as DatasetSceneObject<WebcamFeature>
	}

	/** Rebuild the cluster index from the freshly synced table (visible layers only; hidden ones load on show) */
	public override async refresh(): Promise<void> {
		if (!this.visible && !this.index) return
		await this.loadCatalog()
	}

	/**
	 * Load a camera on demand (history deep-links, search) even when it isn't
	 * interactive yet. Pinned briefly so the focus flight can land before the
	 * cloud's pruning reclaims it.
	 */
	public override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<WebcamFeature> | undefined> {
		const existing = this.getSceneObject(id)
		if (existing) return existing

		const entry = await WebcamStationsTable.getById(id)
		if (!entry) {
			console.warn(`[WebcamsDatasetLayer] Webcam not found: ${id}`)
			return undefined
		}

		const leaf = this.index?.leafOf(id) ?? -1
		if (leaf >= 0) this.cacheEntry(leaf, entry)
		this.pinnedSceneObjects.add(id)
		this.addSceneObject(entry)
		this.materializedAt.set(id, performance.now())
		return this.sceneObjects.get(id)
	}

	override destroy(): void {
		if (this.unsubscribeRedux) {
			this.unsubscribeRedux()
		}
		super.destroy()
	}
}
